import type { WidgetChart, WidgetData } from '@app-types';
import { seriesColor } from './widgetColors';

/** Excel reads a UTF-8 CSV as ANSI unless it opens with a byte order mark, which garbles Cyrillic names. */
const BYTE_ORDER_MARK = '﻿';
const LINE_BREAK = '\r\n';
/** A text cell starting with one of these is run by a spreadsheet as a formula (CSV injection). */
const FORMULA_START = /^[=+\-@\t\r]/;
const PNG_SCALE = 2;
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const LEGEND_HEIGHT = 32;
const LEGEND_SWATCH = 10;
const LEGEND_CHAR_WIDTH = 7.5;
const LEGEND_GAP = 20;

type CsvCell = string | number;

function csvCell(value: CsvCell): string {
  if (typeof value === 'number') {
    return String(value);
  }

  const safe = FORMULA_START.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

function csvSection(title: string, header: CsvCell[], rows: CsvCell[][]): string {
  return [[title], header, ...rows].map((row) => row.map(csvCell).join(',')).join(LINE_BREAK);
}

/**
 * The data of a widget as CSV: its key numbers, its chart data and its table, each under its own title. A part the
 * widget does not have is left out, so a widget with only key numbers exports just those.
 * @param data - What the widget shows
 * @returns The CSV text, starting with a byte order mark
 */
export function buildWidgetCsv(data: WidgetData): string {
  const sections: string[] = [];

  if (data.metrics.length > 0) {
    sections.push(
      csvSection('Key numbers', ['Label', 'Value'], data.metrics.map((metric) => [metric.label, metric.value]))
    );
  }

  if (data.chart && data.chart.categories.length > 0) {
    const { categories, series } = data.chart;
    sections.push(
      csvSection(
        'Chart',
        ['Category', ...series.map((one) => one.name)],
        categories.map((category, index) => [category, ...series.map((one) => one.values[index] ?? 0)])
      )
    );
  }

  if (data.table && data.table.columns.length > 0) {
    sections.push(csvSection('Table', data.table.columns, data.table.rows));
  }

  return BYTE_ORDER_MARK + sections.join(LINE_BREAK + LINE_BREAK);
}

/**
 * A safe download name from a widget title: no path, no odd characters, with the date.
 * @param title - The widget title
 * @param extension - File extension without the dot
 * @param now - The date to put in the name
 */
export function buildExportFileName(title: string, extension: string, now: Date = new Date()): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return `${slug || 'widget'}-${now.toISOString().slice(0, 10)}.${extension}`;
}

/**
 * Save a Blob as a file download.
 * @param blob - The file content
 * @param fileName - The name the browser saves it under
 */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Download the widget's data as a CSV file.
 * @param title - The widget title, used in the file name
 * @param data - What the widget shows
 */
export function exportWidgetCsv(title: string, data: WidgetData): void {
  const blob = new Blob([buildWidgetCsv(data)], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, buildExportFileName(title, 'csv'));
}

export interface SerializedChart {
  markup: string;
  width: number;
  height: number;
}

export interface ChartLegendEntry {
  name: string;
  color: string;
}

/**
 * What the legend of a chart names, in the colours the chart draws them in: the slices of a pie, otherwise the series.
 * @param chart - The chart of a widget
 */
export function chartLegendEntries(chart: WidgetChart): ChartLegendEntry[] {
  const names = chart.type === 'pie' ? chart.categories : chart.series.map((series) => series.name);
  return names.map((name, index) => ({ name, color: seriesColor(index) }));
}

/** Draws the legend in a row, centred, under a chart of the given width starting at the given y. */
function appendLegend(svg: SVGSVGElement, entries: ChartLegendEntry[], width: number, top: number): void {
  const itemWidth = (entry: ChartLegendEntry) => LEGEND_SWATCH + 6 + entry.name.length * LEGEND_CHAR_WIDTH;
  const total = entries.reduce((sum, entry) => sum + itemWidth(entry), 0) + LEGEND_GAP * (entries.length - 1);
  let x = Math.max(8, (width - total) / 2);
  const baseline = top + 20;

  entries.forEach((entry) => {
    const swatch = document.createElementNS(SVG_NAMESPACE, 'rect');
    swatch.setAttribute('x', String(x));
    swatch.setAttribute('y', String(baseline - LEGEND_SWATCH));
    swatch.setAttribute('width', String(LEGEND_SWATCH));
    swatch.setAttribute('height', String(LEGEND_SWATCH));
    swatch.setAttribute('fill', entry.color);

    const label = document.createElementNS(SVG_NAMESPACE, 'text');
    label.setAttribute('x', String(x + LEGEND_SWATCH + 6));
    label.setAttribute('y', String(baseline));
    label.setAttribute('font-size', '13');
    label.setAttribute('fill', '#444444');
    label.textContent = entry.name;

    svg.appendChild(swatch);
    svg.appendChild(label);
    x += itemWidth(entry) + LEGEND_GAP;
  });
}

/**
 * A standalone copy of a chart's SVG that can be drawn as an image: with its namespace, a font, and a white
 * background (a transparent PNG is unreadable on a dark viewer). The legend on the page is HTML next to the SVG, so
 * it is not in the SVG; it is drawn under the chart here, and the picture grows to make room for it. The chart on
 * the page is not changed.
 * @param svg - The chart's SVG element
 * @param legend - What the legend names; none means the chart is exported as it is
 */
export function serializeChartSvg(svg: SVGSVGElement, legend: ChartLegendEntry[] = []): SerializedChart {
  const box = svg.getBoundingClientRect();
  const width = Number(svg.getAttribute('width')) || box.width;
  const chartHeight = Number(svg.getAttribute('height')) || box.height;
  const height = chartHeight + (legend.length > 0 ? LEGEND_HEIGHT : 0);

  const copy = svg.cloneNode(true) as SVGSVGElement;
  copy.setAttribute('xmlns', SVG_NAMESPACE);
  copy.setAttribute('width', String(width));
  copy.setAttribute('height', String(height));
  copy.setAttribute('viewBox', `0 0 ${width} ${height}`);
  copy.setAttribute('font-family', 'sans-serif');

  const background = document.createElementNS(SVG_NAMESPACE, 'rect');
  background.setAttribute('width', '100%');
  background.setAttribute('height', '100%');
  background.setAttribute('fill', '#ffffff');
  copy.insertBefore(background, copy.firstChild);

  if (legend.length > 0) {
    appendLegend(copy, legend, width, chartHeight);
  }

  return { markup: new XMLSerializer().serializeToString(copy), width, height };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The chart could not be drawn as an image.'));
    image.src = url;
  });
}

/**
 * Draw a chart's SVG onto a canvas in the browser and return it as a PNG.
 * @param svg - The chart's SVG element
 * @param legend - What the legend names, drawn under the chart
 */
export async function renderChartPng(svg: SVGSVGElement, legend: ChartLegendEntry[] = []): Promise<Blob> {
  const { markup, width, height } = serializeChartSvg(svg, legend);
  const url = URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml;charset=utf-8' }));

  try {
    const image = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = width * PNG_SCALE;
    canvas.height = height * PNG_SCALE;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('The browser cannot draw the chart as an image.');
    }
    context.scale(PNG_SCALE, PNG_SCALE);
    context.drawImage(image, 0, 0, width, height);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('The PNG could not be created.'))), 'image/png');
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Download the chart as a PNG file made in the browser.
 * @param title - The widget title, used in the file name
 * @param svg - The chart's SVG element
 * @param legend - What the legend names, drawn under the chart
 */
export async function exportChartPng(title: string, svg: SVGSVGElement, legend: ChartLegendEntry[] = []): Promise<void> {
  downloadBlob(await renderChartPng(svg, legend), buildExportFileName(title, 'png'));
}
